<template>
  <LoadingContainer :is-loading="isLoading">
    <div class="card">
      <div class="card-header">
        <div class="card-header-title">
          <div class="title is-6 is-marginless">
            Προϊόντα λίστας
            <span class="tag ml-2">{{ items.length }}</span>
          </div>
        </div>

        <div class="card-header-icon">
          <div class="buttons is-marginless are-small">
            <RouterLink
              :to="{
                name: 'content.home.product-lists.edit',
                params: { id: listId },
              }"
              class="button"
            >
              <span class="icon is-small"><i class="fa fa-chevron-left"/></span>
              <span>Πίσω στο Product List</span>
            </RouterLink>

            <RouterLink
              :to="{
                name: 'content.home.product-lists.items.add',
                params: { id: listId },
              }"
              class="button"
            >
              <span class="icon is-small"><i class="fa fa-plus"/></span>
              <span>Προσθήκη</span>
            </RouterLink>
          </div>
        </div>
      </div>

      <div class="card-content">
        <div class="columns">
          <div class="column is-full">
            <div class="table-wrapper">
              <table
                v-if="items.length"
                class="table is-bordered is-striped is-marginless is-fullwidth"
              >
                <thead>
                  <tr>
                    <th class="has-text-centered" />
                    <th class="has-text-centered">#</th>
                    <th>Φώτο</th>
                    <th>Barcodes</th>
                    <th>Common ID</th>
                    <th>Τίτλος</th>
                    <th>Stock</th>
                    <th>Π.Λ. Τιμή (&euro;)</th>
                    <th>Έκπτωση (&#x25;)</th>
                    <th>Τελική Τιμή (&euro;)</th>
                    <th>Προβολές</th>
                    <th class="hidden-print">Πωλήσεις</th>
                    <th>Product URL</th>
                    <th class="has-text-centered">Ενεργό Προϊόν</th>
                    <th class="has-text-centered">Εμφανές</th>
                    <th class="has-text-centered">Ημ/νια Δημ.</th>
                    <th class="has-text-centered">Ημ/νια Ενημ.</th>
                    <th class="hidden-print">Ενέργειες</th>
                  </tr>
                </thead>

                <draggable
                  v-model="items"
                  tag="tbody"
                  handle=".drag-handle"
                  @change="onReorder"
                >
                  <tr v-for="(item, idx) in items" :key="item.id">
                    <!-- Drag handle -->
                    <td class="has-text-centered">
                      <button
                        v-tooltip="'Σύρετε για αναδιάταξη'"
                        class="button is-small is-white drag-handle"
                        type="button"
                      >
                        <span class="icon is-small">
                          <i class="fa fa-bars" />
                        </span>
                      </button>
                    </td>

                    <!-- Index -->
                    <td class="has-text-centered">{{ idx + 1 }}</td>

                    <!-- Photo -->
                    <td style="width: 80px">
                      <Photo
                        :photos="(item.product && item.product.photos) || []"
                        :width="80"
                        :height="80"
                      />
                    </td>

                    <!-- Barcodes -->
                    <td>
                      <span>{{ barcodesText(item) || '—' }}</span>
                    </td>

                    <!-- Common ID -->
                    <td>
                      <span>
                        {{
                          (takeProduct(item) && takeProduct(item).common_id) ||
                            '—'
                        }}
                      </span>
                    </td>

                    <!-- Title -->
                    <td>
                      <span>
                        {{
                          (takeProduct(item) && takeProduct(item).title) || '—'
                        }}
                      </span>
                    </td>

                    <!-- Stock -->
                    <td>
                      <span>{{ numberText(takeProduct(item), 'stock') }}</span>
                    </td>

                    <!-- Sell Price -->
                    <td>
                      <span>
                        {{ moneyText(takeProduct(item), 'sell_price') }}
                      </span>
                    </td>

                    <!-- Discount -->
                    <td>
                      <span>
                        {{ percentText(takeProduct(item), 'discount') }}
                      </span>
                    </td>

                    <!-- Final Price -->
                    <td>
                      <span>
                        {{ moneyText(takeProduct(item), 'final_price') }}
                      </span>
                    </td>

                    <!-- Views -->
                    <td>
                      <span>{{ numberText(takeProduct(item), 'views') }}</span>
                    </td>

                    <!-- Purchases -->
                    <td class="hidden-print">
                      <span>
                        {{ numberText(takeProduct(item), 'purchases') }}
                      </span>
                    </td>

                    <!-- Product URL -->
                    <td class="link-cell">
                      <a
                        v-if="productUrl(item)"
                        :href="productUrl(item)"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {{ productUrlLabel(item) }}
                      </a>
                      <span v-else>—</span>
                    </td>

                    <!-- Product active badge -->
                    <td class="has-text-centered">
                      <span
                        :class="productActive(item) ? 'is-success' : 'is-light'"
                        class="tag"
                      >
                        {{ productActive(item) ? 'Ναι' : 'Όχι' }}
                      </span>
                    </td>

                    <!-- List item visibility toggle -->
                    <td class="has-text-centered">
                      <ToggleButton
                        :value="!!item.is_active"
                        :sync="true"
                        :color="{ checked: '#22A684', unchecked: '#ff3860' }"
                        :width="30"
                        :height="15"
                        @change="toggleActive(item, $event)"
                      />
                      <p class="help is-size-7 mt-1">
                        Μέχρι 16 ενεργά προϊόντα
                      </p>
                    </td>

                    <!-- Dates -->
                    <td class="has-text-centered">
                      {{ formatDateTime(item.created_at) }}
                    </td>
                    <td class="has-text-centered">
                      {{ formatDateTime(item.updated_at) }}
                    </td>

                    <!-- Actions -->
                    <td class="hidden-print actions-cell has-text-centered">
                      <div class="buttons are-small actions-buttons">
                        <button
                          class="button is-small is-outlined"
                          type="button"
                          title="Διαγραφή"
                          @click="askToDelete(item)"
                        >
                          <span class="icon is-small">
                            <i class="fa fa-trash" />
                          </span>
                        </button>
                      </div>
                    </td>
                  </tr>
                </draggable>
              </table>

              <h4 v-else class="title is-4 has-text-centered">
                Δε βρέθηκαν στοιχεία
              </h4>
            </div>
          </div>
        </div>
      </div>

      <!-- Delete confirmation -->
      <Confirmation
        :model-name="selectedTitle"
        :is-open="isConfirmOpen"
        description="Είστε βέβαιος για τη διαγραφή;"
        @closeModal="onConfirmClose"
      />
    </div>
  </LoadingContainer>
</template>

<script>
import draggable from 'vuedraggable';
import request from '@/utils/request';
import LoadingContainer from '@/views/components/LoadingContainer';
import Confirmation from '@/views/components/Confirmation';
import Photo from '@/views/components/Photo';

export default {
  name: 'ProductListsItemsList',
  components: { draggable, LoadingContainer, Confirmation, Photo },

  props: {
    id: { type: [String, Number], required: true },
  },

  data() {
    return {
      isLoading: false,
      items: [],
      originalSnapshot: [],
      isConfirmOpen: false,
      selected: null,
    };
  },

  computed: {
    listId() {
      return Number(this.id);
    },
    selectedTitle() {
      const p = this.takeProduct(this.selected);
      return p ? p.title : '';
    },
    siteBase() {
      // optional env for public site; empty string if not set
      const base =
        process.env.VUE_APP_SITE_URL || process.env.VUE_APP_FRONT_URL || '';
      return base ? base.replace(/\/+$/, '') : '';
    },
  },

  metaInfo: { title: 'Product List Items' },

  watch: {
    id: {
      immediate: true,
      handler() {
        this.fetch();
      },
    },
  },

  methods: {
    takeProduct(item) {
      return item && item.product ? item.product : null;
    },

    // ---------- Columns helpers ----------
    barcodesText(item) {
      const p = this.takeProduct(item);
      if (!p || !Array.isArray(p.barcodes) || p.barcodes.length === 0) {
        return '';
      }
      const codes = p.barcodes
        .map(b => (b && b.code ? String(b.code) : ''))
        .filter(Boolean);
      return codes.join(', ');
    },

    numberText(product, key) {
      if (!product || product[key] == null) return '—';
      const n = Number(product[key]);
      if (Number.isNaN(n)) return '—';
      return new Intl.NumberFormat('el-GR').format(n);
    },

    moneyText(product, key) {
      if (!product || product[key] == null) return '—';
      const n = Number(product[key]);
      if (Number.isNaN(n)) return '—';
      // Keep it simple; you can switch to style:"currency" if needed.
      return new Intl.NumberFormat('el-GR', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(n);
    },

    percentText(product, key) {
      if (!product || product[key] == null) return '—';
      const n = Number(product[key]);
      if (Number.isNaN(n)) return '—';
      return `${n}%`;
    },

    productActive(item) {
      const p = this.takeProduct(item);
      return !!(p && p.active);
    },

    // ---------- URL helpers ----------
    productUrl(item) {
      const p = this.takeProduct(item);
      if (!p || !p.uuid) return '';
      if (this.siteBase) {
        return `${this.siteBase}/products/${p.uuid}`;
      }
      // fallback to admin route (edit page)
      return `/products/${p.uuid}/edit`;
    },

    productUrlLabel(item) {
      const p = this.takeProduct(item);
      if (!p || !p.uuid) return '';
      if (this.siteBase) {
        return `${this.siteBase}/products/${p.uuid}`;
      }
      // If no public base, show relative admin path just as label
      return `/products/${p.uuid}/edit`;
    },

    // ---------- Fetch / mutations ----------
    async fetch() {
      try {
        this.isLoading = true;
        const { data } = await request.get(
          `/home/product-lists/${this.listId}/items`,
        );
        let list = [];
        if (Array.isArray(data)) {
          list = data;
        } else if (data && Array.isArray(data.items)) {
          list = data.items;
        }
        this.items = list
          .slice()
          .sort((a, b) => (a.order || 0) - (b.order || 0));
        this.snapshot();
      } catch (e) {
        this.$notify({
          type: 'error',
          title: 'Αποτυχία',
          text: 'To αίτημα απέτυχε',
        });
      } finally {
        this.isLoading = false;
      }
    },

    snapshot() {
      this.originalSnapshot = JSON.parse(JSON.stringify(this.items));
    },

    async onReorder() {
      // Backend expects the ROOT array: [ { id, order }, ... ]
      const payload = this.items.map((it, idx) => ({ id: it.id, order: idx }));
      try {
        await request.patch(
          `/home/product-lists/${this.listId}/items/reorder`,
          payload,
        );
        this.items.forEach((it, idx) => this.$set(it, 'order', idx));
        this.snapshot();
        this.$notify({
          type: 'success',
          title: 'Επιτυχία',
          text: 'Η αναδιάταξη ολοκληρώθηκε',
        });
      } catch (e) {
        // rollback
        this.items = JSON.parse(JSON.stringify(this.originalSnapshot));
        const msg =
          e?.response?.data?.message || e?.message || 'To αίτημα απέτυχε';
        this.$notify({ type: 'error', title: 'Αποτυχία', text: msg });
      }
    },

    async toggleActive(item, evt) {
      const hasValue =
        evt && Object.prototype.hasOwnProperty.call(evt, 'value');
      const next = hasValue ? !!evt.value : !item.is_active;
      const prev = !!item.is_active;

      this.$set(item, 'is_active', next);

      try {
        await request.patch(
          `/home/product-lists/${this.listId}/items/${item.id}`,
          { is_active: next },
        );
        this.$notify({
          type: 'success',
          title: 'Επιτυχία',
          text: next
            ? 'Το προϊόν είναι τώρα εμφανές.'
            : 'Το προϊόν δεν είναι πλέον εμφανές.',
        });
      } catch (e) {
        // rollback on failure
        this.$set(item, 'is_active', prev);
        const msg =
          e?.response?.data?.message || e?.message || 'To αίτημα απέτυχε';
        const uiMsg = /16|Maximum of 16/i.test(msg)
          ? 'Μέγιστο 16 ενεργά προϊόντα ανά λίστα'
          : msg;
        this.$notify({
          type: 'error',
          title: 'Αποτυχία',
          text: uiMsg,
        });
      }
    },

    askToDelete(item) {
      this.selected = item;
      this.isConfirmOpen = true;
    },

    async onConfirmClose(confirmed) {
      this.isConfirmOpen = false;
      if (!confirmed || !this.selected) return;

      try {
        const { data } = await request.delete(
          `/home/product-lists/${this.listId}/items/${this.selected.id}`,
        );
        if (data && data.error) throw new Error(data.message || 'Server error');
        this.$notify({
          type: 'success',
          title: 'Επιτυχία',
          text: 'Το προϊόν αφαιρέθηκε από τη λίστα',
        });
        await this.fetch();
      } catch (e) {
        this.$notify({
          type: 'error',
          title: 'Αποτυχία',
          text: 'To αίτημα απέτυχε',
        });
      } finally {
        this.selected = null;
      }
    },

    formatDateTime(v) {
      if (!v) return '-';
      const s = String(v).trim();
      const parts = s.replace('T', ' ').split(' ');
      if (parts.length) {
        const [y, m, d] = (parts[0] || '').split('-');
        const timePart = (parts[1] || '').slice(0, 5);
        if (y && m && d) {
          const pad = n => String(n).padStart(2, '0');
          return `${pad(d)}-${pad(m)}-${y} ${timePart || '00:00'}`;
        }
      }
      const ts = Date.parse(s);
      if (!Number.isNaN(ts)) {
        const dt = new Date(ts);
        const pad = n => String(n).padStart(2, '0');
        return `${pad(dt.getDate())}-${pad(
          dt.getMonth() + 1,
        )}-${dt.getFullYear()} ${pad(dt.getHours())}:${pad(dt.getMinutes())}`;
      }
      return s;
    },
  },
};
</script>

<style scoped>
.table-wrapper {
  position: relative;
}

.drag-handle {
  cursor: move;
}

.actions-cell {
  text-align: right;
  vertical-align: middle;
}

.actions-buttons {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 0;
}

.actions-buttons .button {
  margin: 0;
  min-width: 34px;
  justify-content: center;
  padding-left: 10px;
  padding-right: 10px;
}

.link-cell {
  max-width: 420px;
  white-space: normal;
}

.link-cell a {
  display: inline-block;
  overflow-wrap: anywhere;
  word-break: break-word;
}
</style>
